import React, {useCallback, useEffect, useMemo, useState} from "react";
import { ColumnsType } from "antd/es/table";
import { Button, Input, message, Space, Table } from "antd";
import {numberValidator} from "../../../../utils/Validator";
import {FuelData} from "../../../../types/FuelData";
import {addFuelRow, deleteFuelByFid, fetchFuelByPid, updateFuelRow} from "../../../../backendInteraction/FuelApiFetch";


interface FuelTableProps {
    data: FuelData[];
    plodId: number;
    contractNo: string;
    isView: boolean;
    isAddNewRow: boolean | null;
    setIsAddNewRow: React.Dispatch<React.SetStateAction<boolean>> | null;
}

const FuelTable: React.FC<FuelTableProps> = ( {data, plodId, contractNo, isView, isAddNewRow, setIsAddNewRow })=> {
    const [fuelData, setFuelData] = useState<FuelData[]>([]);
    const [editingFid, setEditingFid] = useState<number | null>(null);
    const [editData, setEditData] = useState<FuelData | null>(null);

    useEffect(() => {
        setFuelData(data);
    }, [data]);

    useEffect(() => {
        if (isAddNewRow === true && editingFid !== -1 ) {
            const newRow: FuelData = {
                key: -1,
                fid: -1,
                loads: "",
                kilometres: "",
                litres: "",
                hours: "",
                driver: "",
                pid: plodId ?? -1,
            }
            setFuelData(prevData => {
                const hasNewRow = prevData.some(item => item.fid === -1);
                return hasNewRow ? prevData : [...prevData, newRow];
            })

            setEditingFid(-1);
            setEditData(newRow);
        }
    }, [isAddNewRow, editingFid]);


    useEffect(() => {
        if (isView) {
            if (editingFid === -1) {
                setFuelData(prevData => prevData.filter(item => item.fid !== -1));
                setIsAddNewRow && setIsAddNewRow(false);
            }

            setEditingFid(null);
            setEditData(null);
        }
    }, [isView])


    const saveChanges = useCallback( async () => {
        console.log("Saving changes:", editData);

        if(!editData) return;

        const { loads, kilometres, litres, hours } = editData;

        if(!!loads) {
            const isNumber = numberValidator(loads, 'Loads');
            if (!isNumber) return;
            if (parseFloat(loads) < 0) {
                message.error('The "Loads" value cannot be less than 0.');
                return;
            }
        }

        if(!!kilometres) {
            const isNumber = numberValidator(kilometres, 'Kilometres');
            if (!isNumber) return;
            if (parseFloat(kilometres) < 0) {
                message.error('The "Kilometres" value cannot be less than 0.');
                return;
            }
        }

        if(!!litres) {
            const isNumber = numberValidator(litres, 'Litres');
            if (!isNumber) return;
            if (parseFloat(litres) < 0) {
                message.error('The "Litres" value cannot be less than 0.');
                return;
            }
        }

        if(!!hours) {
            const isNumber = numberValidator(hours, 'Hours');
            if (!isNumber) return;
            const hoursVal = parseFloat(hours);
            if (hoursVal < 0 || hoursVal > 24) {
                message.error('The "Hours" value is invalid. Please enter a number between 0 and 24.');
                return;
            }
        }

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        if (editingFid === -1) {
            const success = await addFuelRow(editData, aid);
            if (success) {
                setFuelData( await fetchFuelByPid(plodId ?? -1, aid));
                message.success("New fuel added successfully!");
            } else {
                message.error("Failed to add new fuel.");
            }
        } else {
            const success = await updateFuelRow(editingFid ?? -1, editData);
            if (success) {
                setFuelData( await fetchFuelByPid(plodId ?? -1, aid));
                message.success("Fuel updated successfully!");
            } else {
                message.error("Failed to update fuel.");
            }
        }

        if (editingFid === -1 && setIsAddNewRow) {
            setIsAddNewRow(false);
        }

        setEditingFid(null);
        setEditData(null);
    }, [editData, editingFid, setIsAddNewRow]);

    const cancelEditing = useCallback((record: FuelData) => {
        console.log("Cancel editing");
        setEditingFid(null);
        setEditData(null);

        if (record.fid === -1) {
            setFuelData(prevData => prevData.filter(item => item.fid !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }
    }, [setIsAddNewRow]);

    const startEditing = useCallback((record: FuelData) => {
        console.log("Editing row :", record);
        if ( editingFid === -1 ) {
            setFuelData(prevData => prevData.filter(item => item.fid !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }

        setEditingFid( prevId => {
            if (prevId !== null) cancelEditing({fid: prevId} as FuelData);
            return record.fid;
        });

        setEditData({ ...record }) ;
    }, [editingFid, setIsAddNewRow]);

    const deleteRow = useCallback(async (record: FuelData) => {
        console.log("Deleting row:", record);

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        const success = await deleteFuelByFid(record.fid);
        if (success) {
            setFuelData(await fetchFuelByPid(plodId ?? -1, aid));
            message.success("Fuel deleted successfully!");
        } else {
            message.error("Failed to delete fuel.");
        }
    }, []);

    const handleInputChange = useCallback((key: keyof FuelData, value: string) => {
        if (editData) {
            setEditData(prevData => ({
                ...prevData!,
                [key]: value || "",
            }));
        }
    }, [editData]);


    const columns: ColumnsType<FuelData> = useMemo(() => [
        {
            title: "Loads",
            dataIndex: "loads",
            key: "loads",
            width: "16%",
            render: (_, record) =>
                editingFid === record.fid ? (
                    <Input
                        value={editData?.loads}
                        onChange={(e) => handleInputChange("loads", e.target.value)}
                    />
                ) : (
                    record.loads
                ),
        },
        {
            title: 'Kilometres',
            dataIndex: 'kilometres',
            key: 'kilometres',
            width: "16%",
            render: (_, record) =>
                editingFid === record.fid ? (
                    <Input
                        value={editData?.kilometres}
                        onChange={(e) => handleInputChange("kilometres", e.target.value)}
                    />
                ) : (
                    record.kilometres
                ),
        },
        {
            title: 'Litres',
            dataIndex: 'litres',
            key: 'litres',
            width: "16%",
            render: (_, record) =>
                editingFid === record.fid ? (
                    <Input
                        value={editData?.litres}
                        onChange={(e) => handleInputChange("litres", e.target.value)}
                    />
                ) : (
                    record.litres
                ),
        },
        {
            title: 'Hours',
            dataIndex: 'hours',
            key: 'hours',
            width: "16%",
            render: (_, record) =>
                editingFid === record.fid ? (
                    <Input
                        value={editData?.hours}
                        onChange={(e) => handleInputChange("hours", e.target.value)}
                    />
                ) : (
                    record.hours
                ),
        },
        {
            title: 'Driver',
            dataIndex: 'driver',
            key: 'driver',
            width: "16%",
            render: (_, record) =>
                editingFid === record.fid ? (
                    <Input
                        value={editData?.driver}
                        onChange={(e) => handleInputChange("driver", e.target.value)}
                    />
                ) : (
                    record.driver
                ),
        },
        ...(!isView
                ? [{
                    title: 'Action',
                    key: 'action',
                    width: "20%",
                    render: (_: any , record: FuelData) => (
                        <Space size="small">
                            <Space size="small">
                                <Button color="geekblue" variant="text"
                                        onClick={() => (editingFid === record.fid ? saveChanges() : startEditing(record))}>
                                    {editingFid === record.fid ? "Save" : "Edit"}
                                </Button>
                                <Button color="geekblue" variant="text"
                                        onClick={() => (editingFid === record.fid ? cancelEditing(record) : deleteRow(record))}>
                                    {editingFid === record.fid ? "Cancel" : "Delete"}
                                </Button>
                            </Space>
                        </Space>
                    ),
                }] as ColumnsType<FuelData>
                : []
        ),
    ], [isView, editData, handleInputChange, saveChanges, startEditing, cancelEditing, deleteRow]);

    return (
        <Table<FuelData>
            columns={columns}
            pagination={false}
            dataSource={fuelData}
        />
    );
};

export default FuelTable;